// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { format } from "../utils/format";
import instadate from 'instadate';
import { formActions, mergeActionsToProps } from 'redux-pure-form';
import type { MapStateToProps } from 'react-redux';

import history from '../history/history.js';
import { defaultSorting } from '../sorting/sorting.const.js';
import { sortTransactionsInvoices } from '../sorting/sorting.model.js';
import Indication from '../ui/indication.js';
import Icon from '../icon/icon.js';
import Breadcrumbs from '../breadcrumbs/breadcrumbs.js';
import CardsBlock from '../card/cards-block.js';
import Select, { getOption, type Option } from '../form/select.js';
import Table from "./invoices.screen.table";
import type { InputMessages } from '../ui/ui.types.js';

import {
  invoicesSelector,
  invoicesListSelector,
  unpaidInvoicesListSelector,
  invoicesTransactionsListSelector
} from './invoice.selectors.js';

import { eventsSelector } from '../event/event.selectors.js';
import { payerSelector, userSelector } from '../user/user.selectors.js';
import { balanceByOrgListSelector } from '../balance/balance.selectors.js';
import {
  organizationsSelector,
  organizationSelector,
  organizationsOptionsSelector
} from '../organization/organization.selectors.js';
import {
  MODAL_CREDIT,
  EDIT_TRANSACTION,
  MODAL_EDIT_CREDIT
} from '../transaction/transaction.const.js';

import { getInvoices } from './invoice.actions.js';
import {
  getTransactions,
  addCredit,
  saveCredit,
  deleteCredit,
  deleteTransaction
} from '../transaction/transaction.actions.js';
import {
  transactionsSelector,
  transactionsListSelector,
} from '../transaction/transaction.selectors.js';
import { setModal } from '../ui/ui.actions.js';
import { selectUIMessages, selectUIProgress, selectUIModal } from '../ui/ui.helpers.js';
import TransactionCreditModifyModal from '../transaction/transaction-credit-modify.modal.js';
import TransactionModifyModal from '../transaction/transaction-modify.modal.js';

import { monthsOptions } from '../calendar/calendar.consts.js';
import Organization from '../organization/organization.model.js';
import Invoice from './invoice.model.js';
import Calendar from '../calendar/calendar.model.js';
import Url from '../url/url.model.js';
import PayerNameBlock from '../payments/payer-name.block.js';
import InvoicePayBlock from '../invoice/invoice-pay.block.js';

const yearsOptions = Calendar.getYearsOptions();

const transactionsOptions = [
  getOption('All', 'All'),
  getOption('Credits', 'Credits'),
  getOption('Invoices', 'Invoices'),
  getOption('Payments', 'Payments')
];

type OwnProps = {|
  invoices?: boolean,
  transactions?: boolean
|};

type MappedState = {|
  URL: $ElementType<ReduxState, 'URL'>,
  messages: InputMessages,
  progress: boolean,
  user: $ElementType<ReduxState, 'user'>,
  payer: $ElementType<ReduxState, 'payer'>,
  events: $ElementType<ReduxState, 'events'>,
  balanceByOrgList: List,
  organization: $ElementType<ReduxState, 'organization'>,
  organizations: $ElementType<ReduxState, 'organizations'>,
  invoices: $ElementType<ReduxState, 'invoices'>,
  transactions: $ElementType<ReduxState, 'transactions'>,
  invoicesList: List,
  unpaidInvoicesList: List,
  invoicesTransactionsList: []
|};

type Props = {|
  ...OwnProps,
  ...MappedState,
  getInvoices: typeof getInvoices,
  fieldAttrs: FieldAttrs
|};

type State = {|
  filter: {
    month: $ElementType<Option, 'value'>,
    year: $ElementType<Option, 'value'>,
    type: $ElementType<Option, 'value'>
  }
|};

class InvoiceScreen extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const fromDate = this.props.URL.fromDate ? new Date(this.props.URL.fromDate) : new Date();

    this.state = {
      actionType: '',
      transactionId: '',
      filter: {
        organization: 'all',
        month: fromDate.getMonth(),
        year: fromDate.getFullYear(),
        type: 'All'
      },
      credit: {
        id: '',
        productId: '',
        amount: '',
        action: ''
      },
      sorting: defaultSorting
    };

    this.onRefundClick = this.openModal.bind(this, 'refund');
    this.onEditClick = this.openModal.bind(this, 'edit');
    this.onAddClick = this.openModal.bind(this, 'add');
    this.onIncreaseClick = this.openModal.bind(this, 'increase');

    this.deleteTransaction = this.deleteTransaction.bind(this);

    this.onHistoryChange = this.onHistoryChange.bind(this);

    this.onFilterChange = this.onFilterChange.bind(this);
    this.onFilter = this.onFilter.bind(this);

    this.onInvoiceClick = this.onInvoiceClick.bind(this);

    this.onSelectProduct = this.onSelectProduct.bind(this);

    this.onSubmitCredit = this.onSubmitCredit.bind(this);
    this.onDeleteCredit = this.onDeleteCredit.bind(this);
    this.setSorting = this.setSorting.bind(this);
    this.filterSortingInvoicesList = this.filterSortingInvoicesList.bind(this);
  }

  componentDidMount() {
    window.onpopstate = this.onHistoryChange;
  }

  componentWillUnmount() {
    window.onpopstate = null;
  }

  onHistoryChange: Function;
  onHistoryChange() {
    const fromDateUrl = Url.getFromDateFilter();

    const fromDate = fromDateUrl ? new Date(fromDateUrl) : new Date();

    this.setState({
      filter: {
        organization: 'all',
        month: fromDate.getMonth(),
        year: fromDate.getFullYear()
      }
    });

    this.onFilter();
  }

  onFilterChange: Function;
  onFilterChange(e) {
    return this.setState({
      filter: {
        ...this.state.filter,
        [e.currentTarget.name]: e.currentTarget.value
      }
    }, () => {
      this.onFilter();
    });
  }

  onFilter: Function;
  onFilter(e) {
    const filterDate = new Date(
      parseInt(this.state.filter.year, 10),
      parseInt(this.state.filter.month, 10)
    );

    const filterParams = `${format(instadate.firstDateInMonth(filterDate), 'Y-m-d')}/${format(
      instadate.lastDateInMonth(filterDate),
      'Y-m-d'
    )}`;

    if (e && e.currentTarget.id === 'filter') {
      history.push(
        Organization.orgAccess
          ? `/org/${this.props.organization.unique}/invoices/users/${this.props.payer.id}/${filterParams}/`
          : `/invoices/${filterParams}/`
      );
    }

    return this.props.getInvoices(filterParams);
  }

  onInvoiceClick: Function;
  onInvoiceClick(e) {
    const { invoiceId, orgUnique, type } = e.currentTarget.dataset;
    if (e.target.nodeName !== 'A' && type === 'invoice') {
      window.location = Invoice.getInvoiceLink(invoiceId, orgUnique);
    }
  }

  onSubmitCredit: Function;
  onSubmitCredit(e) {
    e.preventDefault();

    return this.state.credit.action === 'add'
      ? this.props.addCredit(this.state.credit)
      : this.props.saveCredit(this.state.credit);
  }

  onDeleteCredit: Function;
  onDeleteCredit(e) {
    return confirm('Are you sure?') ? this.props.deleteCredit(e.currentTarget.id) : false;
  }

  onSelectProduct: Function;
  onSelectProduct(value) {
    return this.setState({
      credit: {
        ...this.state.credit,
        productId: value
      }
    });
  }

  openModal: Function;
  openModal(type, e) {
    const { transactionId } = e.currentTarget.dataset;
    const { transactions } = this.props;
    const transaction = transactions[transactionId];

    switch (type) {
      case 'add':
        this.setState({
          actionType: '',
          transactionId: '',
          credit: {
            ...this.state.credit,
            action: 'add'
          }
        });
        return this.props.setModal(true, MODAL_CREDIT);
      case 'edit':
        this.setState({
          actionType: type,
          transactionId,
          credit: {
            ...this.state.credit,
            action: 'edit'
          }
        });
        return this.props.setModal(
          true,
          transaction.incomeSourceType === 'credit' ? MODAL_EDIT_CREDIT : EDIT_TRANSACTION
        );
      case 'refund':
      case 'increase':
        this.setState({
          actionType: type,
          transactionId
        });
        return this.props.setModal(true, EDIT_TRANSACTION);
    }
  }

  deleteTransaction(e) {
    return confirm('Are you sure?')
      ? this.props.deleteTransaction(e.currentTarget.dataset.transactionId)
      : false;
  }

  setSorting: Function;
  setSorting(e: SyntheticInputEvent<HTMLElement>) {
    const { field } = e.currentTarget.dataset;
    return this.setState({
      sorting: {
        field,
        dir: this.state.sorting.dir === 'asc' ? 'desc' : 'asc'
      }
    });
  }

  filterSortingInvoicesList() {
    const { filter, sorting } = this.state;
    const { invoicesTransactionsList, transactions, invoices, organizations, events } = this.props;

    let filterInvoicesTransactionsList = invoicesTransactionsList;
    if (filter.type === 'Credits')
      filterInvoicesTransactionsList = invoicesTransactionsList.filter(
        item =>
          item.type === 'transaction' &&
          transactions[item.id] &&
          transactions[item.id].directionType === 'income' &&
          transactions[item.id].type === 'Credit'
      );
    if (filter.type === 'Invoices')
      filterInvoicesTransactionsList = invoicesTransactionsList.filter(
        item => item.type === 'invoice'
      );
    if (filter.type === 'Payments')
      filterInvoicesTransactionsList = invoicesTransactionsList.filter(
        item =>
          item.type === 'transaction' &&
          transactions[item.id] &&
          transactions[item.id].directionType === 'outcome'
      );
    if (sorting.field === 'date') {
      return sortTransactionsInvoices(filterInvoicesTransactionsList, invoices, transactions, {
        fieldInvoice: 'userCreatedDateTime',
        fieldTransaction: 'createdDateTime',
        dir: sorting.dir
      });
    }
    if (sorting.field === 'type') {
      return sortTransactionsInvoices(
        filterInvoicesTransactionsList,
        Object.keys(invoices).reduce(
          (acc, invoiceId) => ({ ...acc, [invoiceId]: { type: 'Invoice' } }),
          {}
        ),
        transactions,
        { fieldInvoice: 'type', fieldTransaction: 'type', dir: sorting.dir }
      );
    }
    if (sorting.field === 'organization') {
      return sortTransactionsInvoices(
        filterInvoicesTransactionsList,
        Object.keys(invoices).reduce(
          (acc, invoiceId) => ({
            ...acc,
            [invoiceId]: { organization: organizations[invoices[invoiceId].organizationId].name }
          }),
          {}
        ),
        Object.keys(transactions).reduce(
          (acc, transitionId) => ({
            ...acc,
            [transitionId]: {
              organization: organizations[transactions[transitionId].organizationId].name
            }
          }),
          {}
        ),
        { fieldInvoice: 'organization', fieldTransaction: 'organization', dir: sorting.dir }
      );
    }
    if (sorting.field === 'number') {
      return sortTransactionsInvoices(
        filterInvoicesTransactionsList,
        Object.keys(invoices).reduce(
          (acc, invoiceId) => ({
            ...acc,
            [invoiceId]: { number: `Invoice #${invoices[invoiceId].number}` }
          }),
          {}
        ),
        Object.keys(transactions).reduce(
          (acc, transitionId) => ({
            ...acc,
            [transitionId]: {
              number:
                transactions[transitionId].invoiceId &&
                invoices[transactions[transitionId].invoiceId]
                  ? `For Invoice #${invoices[transactions[transitionId].invoiceId].number}`
                  : ''
            }
          }),
          {}
        ),
        { fieldInvoice: 'number', fieldTransaction: 'number', dir: sorting.dir }
      );
    }
    if (sorting.field === 'event') {
      return sortTransactionsInvoices(
        filterInvoicesTransactionsList,
        Object.keys(invoices).reduce(
          (acc, invoiceId) => ({
            ...acc,
            [invoiceId]: {
              event:
                invoices[invoiceId].eventId && events[invoices[invoiceId].eventId]
                  ? events[invoices[invoiceId].eventId].startTime
                  : 0
            }
          }),
          {}
        ),
        Object.keys(transactions).reduce(
          (acc, transitionId) => ({
            ...acc,
            [transitionId]: {
              event:
                transactions[transitionId].eventId && events[transactions[transitionId].eventId]
                  ? events[transactions[transitionId].eventId].startTime
                  : 0
            }
          }),
          {}
        ),
        { fieldInvoice: 'event', fieldTransaction: 'event', dir: sorting.dir }
      );
    }
    if (sorting.field === 'total') {
      return sortTransactionsInvoices(filterInvoicesTransactionsList, invoices, transactions, {
        fieldInvoice: 'totalPrice',
        fieldTransaction: 'amount',
        dir: sorting.dir
      });
    }
    if (sorting.field === 'paidAmount') {
      return sortTransactionsInvoices(
        filterInvoicesTransactionsList,
        invoices,
        Object.keys(transactions).reduce(
          (acc, transitionId) => ({
            ...acc,
            [transitionId]: { paidAmount: -1 /*Invoice 0 Transaction no exist -1*/ }
          }),
          {}
        ),
        { fieldInvoice: 'paidAmount', fieldTransaction: 'paidAmount', dir: sorting.dir }
      );
    }
    if (sorting.field === 'balance') {
      return sortTransactionsInvoices(
        filterInvoicesTransactionsList,
        invoices,
        Object.keys(transactions).reduce(
          (acc, transitionId) => ({
            ...acc,
            [transitionId]: { balance: -1 /*Invoice 0 Transaction no exist -1*/ }
          }),
          {}
        ),
        { fieldInvoice: 'balance', fieldTransaction: 'balance', dir: sorting.dir }
      );
    }

    return sortTransactionsInvoices(filterInvoicesTransactionsList, invoices, transactions, {
      fieldInvoice: sorting.field,
      fieldTransaction: sorting.field,
      dir: sorting.dir
    });
  }

  filterTransactionsByMonth() {
    const {transactions}= this.props;
    const {filter = {} } = this.state;
    const {month} = filter;
    const monthInFilter = Number(this.state.filter.month);
    const yearInFilter = Number(this.state.filter.year);

    if (month) {
      const filteredTransactions = {};

      Object.keys(transactions).forEach((transactionId) => {
        const transaction = transactions[transactionId];
        const transactionCreatedAt = new Date(transaction.createdDateTime);
        const transactionMonth = transactionCreatedAt.getMonth();
        const transactionYear = transactionCreatedAt.getFullYear();

        if (transactionMonth === monthInFilter && transactionYear === yearInFilter) {
          filteredTransactions[transactionId] = transaction;
        }
      })

      return filteredTransactions;
    } else {
      return transactions;
    }
  }

  render() {
    const {
      URL,
      progress,
      messages,
      user,
      payer,
      events,
      balanceByOrgList,
      organization,
      organizations,
      invoices,
      accessgroups,
    } = this.props;

    if (!payer) return null;

    const transactionsCurrentMonth = this.filterTransactionsByMonth();
    const invoicesListFilter = this.filterSortingInvoicesList();

    return (
      <div>
        <Breadcrumbs />

        <Indication closable messages={messages} className="message-indicator" />

        {this.state.credit.action ? (
          <TransactionCreditModifyModal
            type={this.state.credit.action}
            transactionId={this.state.transactionId}
          />
        ) : null}
        <TransactionModifyModal
          actionType={this.state.actionType}
          transactionId={this.state.transactionId}
        />

        <div className="invoices-container">
          {URL.org ? (
            <PayerNameBlock
              payer={payer}
              organization={organizations[organization.id]}
              payerBalance={payer.balanceByOrg[organization.id]}
            />
          ) : (
            <CardsBlock>
              <div className="payer-info">
                <div className="payer-balance-item-wrap">
                  {balanceByOrgList.map(orgId =>
                    organizations[orgId] ? (
                      <div key={`balance-org-${orgId}`} className="payer-balance-item">
                        <span className="payer-balance-item-label">
                          {organizations[orgId].name} Balance
                        </span>
                        <span className="payer-balance">${payer.balanceByOrg[orgId].balance}</span>
                      </div>
                    ) : null
                  )}
                  <div className="payer-balance-item">
                    <span className="payer-balance-item-label">Total Balance</span>
                    <span className="payer-balance">
                      $
                      {balanceByOrgList.reduce(
                        (acc, orgId) => acc + parseFloat(payer.balanceByOrg[orgId].balance),
                        0
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </CardsBlock>
          )}
          {Organization.orgAccess ? (
            <div className="create-invoice-wrap-btn">
              <a
                className="btn btn-xs btn-success"
                href={`${window.serverData._uri}/org/${organization.unique}/invoices/create/user/${payer.id}/`}
              >
                Create Invoice
              </a>
            </div>
          ) : null}
          <div className="invoices-unpaid-block">
            <InvoicePayBlock showFilter />
          </div>

          <div className="invoices-paid-block">
            <div className="heading-block">
              <div className="heading-block-name">
                <Icon width="12px" height="14px" name="card" className="icon-primary" />
                <span className="heading-text">Invoices/Transactions</span>
              </div>
            </div>

            <div className="panel panel-default invoices-filter-block">
              <div className="filer-item filter-lebel">Filter by Month</div>

              <div className="filer-item select">
                <Select
                  name="month"
                  className="form-control"
                  options={monthsOptions}
                  value={this.state.filter.month}
                  onChange={this.onFilterChange}
                />
              </div>

              <div className="filer-item select">
                <Select
                  name="year"
                  className="form-control"
                  options={yearsOptions}
                  value={this.state.filter.year}
                  onChange={this.onFilterChange}
                />
              </div>

              <button
                id="filter"
                className="btn btn-success"
                onClick={this.onFilter}
                disabled={progress}
              >
                <span className="icon search" /> Filter
              </button>
              <div className="filer-item filter-lebel filter-lebel-right">
                Filter by Invoice/Transaction Type
              </div>
              <div className="filer-item select">
                <Select
                  name="type"
                  className="form-control"
                  options={transactionsOptions}
                  value={this.state.filter.type}
                  onChange={this.onFilterChange}
                />
              </div>
            </div>
            {Organization.orgAccess ? (
              <button className="btn btn-xs btn-success btn-add-card" onClick={this.onAddClick}>
                Add credit
              </button>
            ) : null}

            {invoicesListFilter.length ? (
              <Table
                URL={URL}
                user={user}
                events={events}
                list={invoicesListFilter}
                invoices={invoices}
                transactions={transactionsCurrentMonth}
                organizations={organizations}
                calendarView={accessgroups.calendarView}
                showOrgColumn={user.id === payer.id || !Organization.orgAccess}
                onInvoiceClick={this.onInvoiceClick}
                onRefundClick={this.onRefundClick}
                onEditClick={this.onEditClick}
                onIncreaseClick={this.onIncreaseClick}
                deleteTransaction={this.deleteTransaction}
                setSorting={this.setSorting}
                sorting={this.state.sorting}
              />
            ) : (
              <div className="panel panel-default no-panel-block">
                <h4 className="text-center no-panel-text">
                  There aren't any invoices for the selected month yet.
                </h4>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const progressSelector = selectUIProgress();
const messagesSelector = selectUIMessages();
const modalSelector = selectUIModal(MODAL_CREDIT);

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  URL: state.URL,
  progress: progressSelector(state),
  messages: messagesSelector(state),
  user: userSelector(state),
  payer: payerSelector(state),
  events: eventsSelector(state),
  balanceByOrgList: balanceByOrgListSelector(state),
  organization: organizationSelector(state),
  organizations: organizationsSelector(state),
  organizationsOptions: organizationsOptionsSelector(state),
  invoices: invoicesSelector(state),
  invoicesList: invoicesListSelector(state),
  transactions: transactionsSelector(state),
  transactionsList: transactionsListSelector(state),
  unpaidInvoicesList: unpaidInvoicesListSelector(state),
  invoicesTransactionsList: invoicesTransactionsListSelector(state),
  accessgroups: state.accessgroups,
  addCreditModal: modalSelector(state)
});

export default connect(
  mapStateToProps,
  {
    ...formActions,
    getTransactions,
    getInvoices,
    addCredit,
    saveCredit,
    deleteCredit,
    setModal,
    deleteTransaction
  },
  mergeActionsToProps
)(InvoiceScreen);
